import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { store } from 'src';
import { Action, Dispatch } from 'redux';
import { AppState } from '..';
import { QuoteState } from './quote.reducer';
// import { ContractViewModel } from '@services/ContractService';
import { convertQuoteToContract, resetContract } from '../contract/contract.actions';
import { quoteService } from '@services/QuoteService';
import { QuoteViewModel, QuoteSearchViewModel } from '@models/Quote';
import { PageViewModel } from '@models/common.models';
import { ContractViewModel } from '@models/Contract';
/*
These are the actions for the page.
Think of these as 'event emitters' used by the components on the page.
*/

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum QuoteActionType {
  QuoteAction = 'QuoteAction',
  GetQuotesStart = 'GetQuotesStart',
  GetQuotesSuccess = 'GetQuotesSuccess',
  GetQuotesFailure = 'GetQuotesFailure',
  ResetQuotes = 'ResetQuotes',
  SaveQuoteStart = 'SaveQuoteStart',
  SaveQuoteSuccess = 'SaveQuoteSuccess',
  SaveQuoteFailure = 'SaveQuoteFailure',
  ResetPriceAdjustments = 'ResetPriceAdjustments',

  /* Get Details */
  QuoteGetDetailsStart = 'QuoteGetDetailsStart',
  QuoteGetDetailsSuccess = 'QuoteGetDetailsSuccess',
  QuoteGetDetailsFailure = 'QuoteGetDetailsFailure',

  SendQuoteEmailStart = 'SendQuoteEmailStart',
  SendQuoteEmailSuccess = 'SendQuoteEmailSuccess',
  SendQuoteEmailFailure = 'SendQuoteEmailFailure',
  ConvertQuotePreventReset = 'ConvertQuotePreventReset',

  ResetForm = 'ResetForm',
  ClearQuoteState = 'ClearQuoteState'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Search **********/
export interface GetQuotesStartAction extends Action {
  readonly query: string | null;
  readonly filterByConverted: boolean;
  readonly type: QuoteActionType.GetQuotesStart;
}

export interface GetQuotesSuccessAction extends Action {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<QuoteSearchViewModel>;
  readonly type: QuoteActionType.GetQuotesSuccess;
}

export interface GetQuotesFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: QuoteActionType.GetQuotesFailure;
}

export interface ResetQuotesAction extends Action {
  readonly type: QuoteActionType.ResetQuotes;
}

export interface ClearQuoteStateAction extends Action {
  readonly type: QuoteActionType.ClearQuoteState;
}

/********* Save ***********/
export interface SaveQuoteStartAction extends Action {
  readonly type: QuoteActionType.SaveQuoteStart;
  readonly message: string;
}

export interface SaveQuoteSuccessAction extends Action {
  readonly type: QuoteActionType.SaveQuoteSuccess;
  readonly message: string;
  readonly quoteId: number;
}

export interface SaveQuoteFailureAction extends Action {
  readonly type: QuoteActionType.SaveQuoteFailure;
  readonly errorMessage: string;
}

export interface GetQuoteDetailsStartAction extends Action {
  readonly type: QuoteActionType.QuoteGetDetailsStart;
  readonly quoteId: number;
}

export interface GetQuoteDetailsSuccessAction extends Action {
  readonly type: QuoteActionType.QuoteGetDetailsSuccess;
  readonly quoteVM: QuoteViewModel;
}

export interface GetQuoteDetailsFailureAction extends Action {
  readonly type: QuoteActionType.QuoteGetDetailsFailure;
  readonly errorMessage: string;
  readonly quoteId: number;
}

export interface SendQuoteEmailStartAction extends Action {
  readonly type: QuoteActionType.SendQuoteEmailStart;
  readonly quoteId: number;
  readonly email: string;
}

export interface SendQuoteEmailSuccessAction extends Action {
  readonly type: QuoteActionType.SendQuoteEmailSuccess;
}

export interface SendQuoteEmailFailureAction extends Action {
  readonly type: QuoteActionType.SendQuoteEmailFailure;
  readonly errorMessage: string;
}

export interface ResetFormAction extends Action {
  readonly type: QuoteActionType.ResetForm;
}

export interface ConvertQuotePreventResetAction extends Action {
  readonly type: QuoteActionType.ConvertQuotePreventReset;
  readonly preventReset: boolean;
}

export interface ResetQuotePriceAdjustmentsAction extends Action {
  readonly type: QuoteActionType.ResetPriceAdjustments;
}

export function clearQuoteState(): ClearQuoteStateAction {
  return {
    type: QuoteActionType.ClearQuoteState
  };
}

/********** Search **********/
export const getQuotes = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  filterByConverted: boolean
) => {
  return async (dispatch: Dispatch<QuoteState>) => {
    dispatch(getQuotesStart(query, filterByConverted));
    try {
      let result = await quoteService.searchQuotes(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterByConverted
      );
      dispatch(getQuotesSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(getQuotesFailure(e.toString()));
    }
  };
};

export function getQuotesStart(query: string | null, filterByConverted: boolean): GetQuotesStartAction {
  return {
    query: query,
    filterByConverted: filterByConverted,
    type: QuoteActionType.GetQuotesStart
  };
}

export function getQuotesSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<QuoteSearchViewModel>
): GetQuotesSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: QuoteActionType.GetQuotesSuccess
  };
}

export function getQuotesFailure(errorMessage: string): GetQuotesFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search quotes. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: QuoteActionType.GetQuotesFailure
  };
}

export function reset(): ResetQuotesAction {
  return {
    type: QuoteActionType.ResetQuotes
  };
}

/************* Save ***********/
export function saveQuote(model: ContractViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      dispatch(saveQuoteStart());
      const newQuoteId = await quoteService.saveQuote(model);
      return dispatch(saveQuoteSuccess(newQuoteId));
    } catch (e) {
      return dispatch(saveQuoteFailure(e.message));
    }
  };
}

export function saveQuoteStart(): SaveQuoteStartAction {
  return {
    type: QuoteActionType.SaveQuoteStart,
    message: 'Saving Quote Start'
  };
}

export function saveQuoteSuccess(quoteId: number): SaveQuoteSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message={`Quote Saved Successfully`} />, ToastOptions.Success);

  return {
    type: QuoteActionType.SaveQuoteSuccess,
    message: 'Quote Saved Successfully',
    quoteId: quoteId
  };
}

export function saveQuoteFailure(errorMessage: string): SaveQuoteFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);

  return {
    type: QuoteActionType.SaveQuoteFailure,
    errorMessage: errorMessage
  };
}

export function getQuoteDetails(quoteId?: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    if (quoteId) {
      const quoteState = getState().quote;

      if (quoteState.loadingQuoteDetailsFor.indexOf(quoteId) !== -1) {
        return;
      }

      dispatch(getQuoteDetailsStart(quoteId));
      quoteService
        .getQuote(quoteId)
        .then(quote => {
          dispatch(getQuoteDetailsSuccess(quote));
          return;
        })
        .catch(err => {
          dispatch(getQuoteDetailsFailure(quoteId, err.message));
          return;
        });
    }
  };
}

export function getQuoteDetailsStart(quoteId: number): GetQuoteDetailsStartAction {
  return {
    type: QuoteActionType.QuoteGetDetailsStart,
    quoteId: quoteId
  };
}

export function getQuoteDetailsSuccess(quote: QuoteViewModel): GetQuoteDetailsSuccessAction {
  return {
    type: QuoteActionType.QuoteGetDetailsSuccess,
    quoteVM: quote
  };
}

export function getQuoteDetailsFailure(quoteId: number, errorMessage: string): GetQuoteDetailsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search quotes. ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: QuoteActionType.QuoteGetDetailsFailure,
    quoteId: quoteId,
    errorMessage: errorMessage
  };
}

export function loadQuoteIntoFlow(quote: QuoteViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    // change this if convert quote ever sets a flag on the quote, or if the contract/quote flows get separated.
    dispatch(convertQuoteToContract(quote));
  };
}

export function clearQuotePriceAdjustments(): ResetQuotePriceAdjustmentsAction {
  return {
    type: QuoteActionType.ResetPriceAdjustments
  };
}

export function convertQuotePreventReset(preventReset: boolean): ConvertQuotePreventResetAction {
  return {
    type: QuoteActionType.ConvertQuotePreventReset,
    preventReset: preventReset
  };
}
/**************** Send Quote Email ********************/

export function sendQuoteEmail(quoteId: number, email: string) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(sendQuoteEmailStart(quoteId, email));
    try {
      await quoteService.emailQuote(quoteId, email);
      dispatch(sendQuoteEmailSuccess());
    } catch (e) {
      dispatch(sendQuoteEmailFailure(e.toString()));
    }
  };
}

/*export async function sendQuoteEmail(quoteId: number, email: string) {
    await quoteService.emailQuote(quoteId, email);

} */

export function sendQuoteEmailStart(quoteId: number, email: string): SendQuoteEmailStartAction {
  return {
    type: QuoteActionType.SendQuoteEmailStart,
    quoteId: quoteId,
    email: email
  };
}
export function sendQuoteEmailSuccess(): SendQuoteEmailSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message={'Quote Sent Successfully'} />, ToastOptions.Success);

  return {
    type: QuoteActionType.SendQuoteEmailSuccess
  };
}
export function sendQuoteEmailFailure(errorMessage: string): SendQuoteEmailFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to email Quote. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: QuoteActionType.SendQuoteEmailFailure,
    errorMessage: errorMessage
  };
}

export function resetQuote() {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(resetContract(true));
  };
}

export function getCurrentQuoteId(): number {
  // NOTE: THIS IS ONLY USED TO GET THE QUOTE ID FOR THE REDIRECT ON THE WIZARD AFTER CREATING A NEW QUOTE - USE FOR ABSOLUTELY NOTHING ELSE
  return store.getState().quote.id;
}



// WEBPACK FOOTER //
// ./src/quote/quote.actions.tsx